import React, { useEffect, useRef } from 'react';
import { Badge } from '../../components/ui/badge';
import {
  CONSEQUENCE_DETAIL_INCOMPLETE,
  actionOutcomeDisplay,
  announceAction,
  holdReasonDisplay,
} from './authorityPresentation';
import { AuthorityRefusal } from './AuthorityRefusal';
import { AuthorityAnnouncer } from './AuthorityAnnouncer';

/**
 * WHAT ACTUALLY HAPPENED TO ONE OPERATOR ACTION, AND NOTHING MORE.
 *
 * This replaces a toast. Two reasons, both load-bearing:
 *
 *   1. A toast said `Action ${status}` through `toast.success()` — so an
 *      action that came back status:"failed" or status:"refused" was announced
 *      in the success colour. The HTTP call succeeded; the action did not.
 *   2. A toast disappears. A consequential outcome — something was written, or
 *      deliberately was not — must still be on screen when the user looks back,
 *      and must survive a re-render.
 *
 * Keyed by action id by the parent, so two invocations of the SAME tool do not
 * share one outcome. Nothing here decides anything: `outcome` comes from
 * presentActionResponse reading the body's `status`, and a refusal comes from
 * the shared classifier.
 *
 * The OUTER container and the announcer are mounted unconditionally, even
 * before any answer exists. That is not tidiness — see AuthorityAnnouncer for
 * why a live region that arrives with its own text announces nothing, and see
 * the focus effect below for why the focus target must not be a node that
 * disappears at the moment it is needed.
 */
/**
 * Tones keyed by the presenter's OWN outcome keys. An earlier draft invented
 * `AWAITING_HUMAN` and `IN_PROGRESS`, which silently fell through to the
 * NOT_STATED amber -- a held action wearing the colour of an unreadable one.
 * A browser test caught it; the lesson is that this map must never restate the
 * vocabulary from memory.
 *
 * Note EXECUTED is the ONLY green. A refusal is not a failure and is not a
 * success, so it is neutral, and "awaiting a human" is indigo rather than
 * green because nothing has happened yet.
 *
 * Every tone is a LIGHT surface with dark text, on an app whose global
 * `--foreground` is pure white. Anything inside that inherits `text-foreground`
 * is therefore invisible here -- which is what happened to the outcome label
 * (see the Badge below).
 */
const TONE = {
  EXECUTED: 'bg-emerald-50 border-emerald-300 text-emerald-900',
  FAILED: 'bg-rose-50 border-rose-300 text-rose-900',
  REFUSED: 'bg-slate-50 border-slate-300 text-slate-900',
  DENIED: 'bg-slate-50 border-slate-300 text-slate-900',
  AWAITING_CONFIRMATION: 'bg-indigo-50 border-indigo-300 text-indigo-900',
  PREPARED: 'bg-slate-50 border-slate-300 text-slate-900',
  EXECUTING: 'bg-slate-50 border-slate-300 text-slate-900',
  NOT_STATED: 'bg-amber-50 border-amber-300 text-amber-900',
};

export const OperatorActionOutcome = ({ outcome, refusal, holdRisk, onConfirm, onDeny, busy }) => {
  const display = outcome ? actionOutcomeDisplay(outcome.outcome?.key) : null;
  const awaiting = display?.key === 'AWAITING_CONFIRMATION';
  const hold = awaiting ? holdReasonDisplay(holdRisk) : null;
  const spoken = announceAction({
    outcomeKey: outcome?.outcome?.key || null,
    toolName: outcome?.toolName || null,
    refusal,
  });

  /**
   * FOCUS SURVIVES THE ANSWER.
   *
   * Confirm and Deny are the only controls here, and the answer to pressing
   * one of them is that both stop existing. A keyboard or screen-reader user
   * pressed a button and focus fell to <body>: the announcement plays, and
   * then they are at the top of the document with no idea where the result is.
   *
   * So focus moves to this container -- but ONLY when the user's own act
   * caused the change AND focus was actually lost. A background response for
   * some other action must never steal focus from what the user is doing.
   */
  const containerRef = useRef(null);
  const userActed = useRef(false);
  const settleKey = refusal
    ? `refusal:${refusal.kind}:${refusal.code || ''}`
    : `outcome:${display?.key || 'none'}`;

  useEffect(() => {
    if (!userActed.current) return;
    if (typeof document === 'undefined') return;
    const active = document.activeElement;
    const lost = !active || active === document.body || !document.contains(active);
    if (lost) {
      // Repair it HERE, on the render that removed the control -- not once
      // the request bookkeeping finishes.
      //
      // Measured: gating this on `busy` going false put the repair behind
      // decideAction's two post-answer reloads (loadSession, loadSessions),
      // so focus sat on <body> for as long as those took. Desktop won that
      // race and mobile lost it, which is the only reason it was caught at
      // all -- on a slow connection every user would have lost it.
      userActed.current = false;
      if (containerRef.current) containerRef.current.focus();
      return;
    }
    // Focus is still on something real -- a transport failure, say, leaves
    // the controls mounted and the user standing on one. Nothing to repair;
    // but once the request is over, disarm, so a later unrelated settle
    // cannot inherit this act and yank focus out of whatever the user has
    // moved on to.
    if (!busy) userActed.current = false;
  }, [settleKey, busy]);

  const act = (handler) => () => {
    if (busy) return; // aria-disabled is advisory; the guard is here
    userActed.current = true;
    handler();
  };

  return (
    <div
      ref={containerRef}
      tabIndex={-1}
      data-testid="operator-action-region"
      className="rounded-md focus:outline-none focus:ring-2 focus:ring-sky-400"
    >
      <AuthorityAnnouncer message={spoken.message} urgent={spoken.urgent} />

      {/* A refusal with NO outcome settles the action: that is a governed
          answer, and the decision is over. A refusal WITH an outcome is a
          transport failure -- the server never gave a governed answer, so the
          action is still held and its controls must survive. Both are shown;
          neither replaces the other. */}
      {refusal && (
        <div data-testid="operator-action-refusal">
          <AuthorityRefusal refusal={refusal} />
        </div>
      )}

      {display && (
        <div
          data-testid="operator-action-outcome"
          data-outcome={display.key}
          data-executed={outcome.executed ? 'true' : 'false'}
          className={`rounded-md border p-3 space-y-2 ${refusal ? 'mt-2 ' : ''}${
            TONE[display.key] || TONE.NOT_STATED
          }`}
        >
          <div className="flex flex-wrap items-center gap-2">
            {/* text-current/border-current, NOT the `outline` variant's own
                `text-foreground`. --foreground is 0 0% 100% in this theme, so
                the outline badge rendered WHITE ON bg-emerald-50 -- the single
                piece of text that names what happened to a consequential
                write was invisible. cn() runs twMerge, so the later text-*
                class wins over the variant's. */}
            <Badge
              variant="outline"
              className="text-current border-current"
              data-testid="outcome-label"
            >
              {display.label}
            </Badge>
            {outcome.toolName && (
              <span className="text-xs font-mono opacity-80">{outcome.toolName}</span>
            )}
          </div>
          <p className="text-sm">{display.meaning}</p>

          {hold && (
            <div
              data-testid="operator-hold-reason"
              data-risk={holdRisk || 'unstated'}
              className="space-y-1"
            >
              {/* heading/lead — the field names holdReasonDisplay actually
                  returns. An earlier draft read .label/.meaning and rendered
                  TWO EMPTY PARAGRAPHS, so a model-routed hold was silently
                  unnamed: the very thing this block exists to say. Component
                  tests now render every branch and assert the text is
                  non-empty. */}
              <p className="text-sm font-medium">{hold.heading}</p>
              <p className="text-xs">{hold.lead}</p>
              {/* The backend preview is not a consequence snapshot yet.
                  Approving on a tool name alone is not informed authorization,
                  and saying so is honest rather than obstructive — it does not
                  disable the control, because the current contract does not
                  require that. */}
              <p data-testid="consequence-caveat" className="text-xs italic">
                {CONSEQUENCE_DETAIL_INCOMPLETE.meaning}
              </p>
            </div>
          )}

          {/* The server's own sentence, verbatim, never parsed. */}
          {outcome.error && (
            <p data-testid="outcome-server-message" className="text-xs opacity-90">
              {outcome.error}
            </p>
          )}

          {awaiting && (onConfirm || onDeny) && (
            <div className="flex flex-wrap gap-2 pt-1">
              {/* aria-disabled, not `disabled`. A natively disabled button
                  leaves the tab order the instant it is pressed, so the
                  keyboard user who just authorized a consequential write is
                  dropped to <body> mid-request. aria-disabled keeps the
                  control focusable and announced as unavailable, and the
                  actual double-submit guard is synchronous in the caller. */}
              {onConfirm && (
                <button
                  type="button"
                  data-testid="outcome-confirm"
                  aria-disabled={busy ? 'true' : 'false'}
                  aria-busy={busy ? 'true' : 'false'}
                  onClick={act(onConfirm)}
                  className="rounded border border-indigo-400 px-2 py-1 text-xs font-medium aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
                >
                  Confirm and execute
                </button>
              )}
              {onDeny && (
                <button
                  type="button"
                  data-testid="outcome-deny"
                  aria-disabled={busy ? 'true' : 'false'}
                  aria-busy={busy ? 'true' : 'false'}
                  onClick={act(onDeny)}
                  className="rounded border border-slate-400 px-2 py-1 text-xs font-medium aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
                >
                  Deny
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OperatorActionOutcome;
